import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { band, tracks } from '@/constants/portfolio'
import { useExperience } from '@/components/generals/experience-provider'
import TrackRow from '@/components/landing/track-row'

// Music identity on the stage-night background: the statement on the left,
// the slanted three-song sampler on the right.
const MusicSection: FC = (): ReactNode => {
    const { immersive } = useExperience()
    const listRef = useRef<HTMLOListElement>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        if (!immersive) {
            setVisible(true)
            return
        }

        const list = listRef.current
        if (!list) return

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setVisible(true)
                observer.disconnect()
            }
        }, { rootMargin: '0px 0px -15% 0px' })

        observer.observe(list)
        return () => observer.disconnect()
    }, [immersive])

    return (
        <section id='music' data-theme='night' aria-labelledby='music-title' className='relative overflow-hidden bg-night text-paper'>
            <div aria-hidden='true' className='halftone-light pointer-events-none absolute -right-[10vw] top-0 h-[60%] w-[46vw] -skew-x-12 opacity-[0.08]' />
            <div aria-hidden='true' className='pointer-events-none absolute -left-[6vw] bottom-[-6vw] select-none'>
                <span className='display-type block text-[26vw] text-paper/[0.035]'>Muzik</span>
            </div>

            <div className='relative mx-auto grid max-w-[1680px] gap-14 px-5 py-24 xs:px-8 tab:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] tab:gap-16 tab:px-12 tab:py-32 wide:gap-24'>
                <div className='tab:sticky tab:top-28 tab:self-start'>
                    <p className='mono-label text-[11px] text-night-text'>03 / Music identity</p>
                    <h2 id='music-title' className='display-type mt-3 text-[clamp(3rem,6vw,5.6rem)]'>Music</h2>

                    <p className='mt-8 text-[clamp(1.25rem,1.9vw,1.75rem)] font-medium leading-[1.4] tracking-[-0.01em]'>
                        {band.musicIdentityText}
                    </p>

                    <ul className='mt-8 flex flex-wrap gap-2' aria-label='Genres'>
                        {band.genres.map(genre => (
                            <li key={genre} className='mono-label border border-paper/30 px-3 py-1.5 text-[10.5px] text-night-text'>
                                {genre}
                            </li>
                        ))}
                    </ul>

                    <p className='mt-10 max-w-[46ch] text-[15px] leading-[1.6] text-night-text'>
                        Want the full songs and the live energy? Follow the band on{' '}
                        <a
                            href={band.socialLinks.instagram}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-semibold text-paper underline decoration-night-text decoration-2 underline-offset-4 hover:decoration-paper'
                        >
                            Instagram {band.socialLinks.instagramHandle}
                        </a>
                        .
                    </p>
                </div>

                <div>
                    <div className='flex items-end justify-between gap-4 border-b-2 border-paper/80 pb-3'>
                        <p className='display-type text-[clamp(1.4rem,2.2vw,2rem)]'>Sampler</p>
                        <p className='mono-label text-[10.5px] text-night-text'>{tracks.length} songs / one plays at a time</p>
                    </div>

                    <ol ref={listRef} className='divide-y divide-paper/10'>
                        {tracks.map((track, index) => (
                            <TrackRow key={track.id} track={track} index={index} visible={visible} />
                        ))}
                    </ol>

                    <p className='mono-label mt-6 text-[10px] leading-relaxed text-night-text/90'>
                        Tip: focus a progress bar and use the arrow keys to skip 5 seconds.
                    </p>
                </div>
            </div>
        </section>
    )
}

export default MusicSection
